"use client"

import { useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Copy, Check, Lock, Unlock, Trash2, Calculator } from "lucide-react"
import { toast } from "sonner"

interface PresetRatio {
  name: string
  width: number
  height: number
  description: string
}

const presetRatios: PresetRatio[] = [
  { name: "16:9", width: 16, height: 9, description: "HD Video, YouTube" },
  { name: "4:3", width: 4, height: 3, description: "Classic TV, iPad" },
  { name: "21:9", width: 21, height: 9, description: "Ultrawide, Cinema" },
  { name: "1:1", width: 1, height: 1, description: "Square, Instagram" },
  { name: "9:16", width: 9, height: 16, description: "Vertical Video, TikTok" },
  { name: "3:2", width: 3, height: 2, description: "Classic Film, DSLR" },
  { name: "2:3", width: 2, height: 3, description: "Portrait Photo" },
  { name: "4:5", width: 4, height: 5, description: "Instagram Portrait" },
  { name: "5:4", width: 5, height: 4, description: "Large Format" },
  { name: "1.85:1", width: 185, height: 100, description: "US Widescreen Cinema" },
  { name: "2.35:1", width: 235, height: 100, description: "Anamorphic Cinema" },
  { name: "2.39:1", width: 239, height: 100, description: "Modern Cinema Scope" },
]

function gcd(a: number, b: number): number {
  a = Math.abs(Math.round(a))
  b = Math.abs(Math.round(b))
  while (b) {
    const t = b
    b = a % b
    a = t
  }
  return a
}

function simplifyRatio(width: number, height: number): { w: number; h: number } {
  const divisor = gcd(width, height)
  return {
    w: Math.round(width / divisor),
    h: Math.round(height / divisor),
  }
}

export function AspectRatioCalculator() {
  const [width, setWidth] = useState<string>("1920")
  const [height, setHeight] = useState<string>("1080")
  const [ratioW, setRatioW] = useState<number>(16)
  const [ratioH, setRatioH] = useState<number>(9)
  const [locked, setLocked] = useState<"width" | "height" | null>(null)
  const [copied, setCopied] = useState<string | null>(null)

  const updateRatio = (nextWidth: number, nextHeight: number) => {
    if (nextWidth > 0 && nextHeight > 0) {
      const { w: simplifiedWidth, h: simplifiedHeight } = simplifyRatio(nextWidth, nextHeight)
      setRatioW(simplifiedWidth)
      setRatioH(simplifiedHeight)
    }
  }

  const handleWidthChange = (value: string) => {
    const nextWidth = parseFloat(value)
    let nextHeight = parseFloat(height)
    setWidth(value)

    if (locked === "height" && nextWidth > 0 && ratioW > 0 && ratioH > 0) {
      nextHeight = Math.round((nextWidth * ratioH) / ratioW)
      setHeight(nextHeight.toString())
    }

    updateRatio(nextWidth, nextHeight)
  }

  const handleHeightChange = (value: string) => {
    let nextWidth = parseFloat(width)
    const nextHeight = parseFloat(value)
    setHeight(value)

    if (locked === "width" && nextHeight > 0 && ratioW > 0 && ratioH > 0) {
      nextWidth = Math.round((nextHeight * ratioW) / ratioH)
      setWidth(nextWidth.toString())
    }

    updateRatio(nextWidth, nextHeight)
  }

  // Apply preset ratio
  const applyPreset = (preset: PresetRatio) => {
    setRatioW(preset.width)
    setRatioH(preset.height)

    const w = parseFloat(width)
    const h = parseFloat(height)

    if (locked === "width" && w > 0) {
      const newHeight = (w * preset.height) / preset.width
      setHeight(Math.round(newHeight).toString())
    } else if (locked === "height" && h > 0) {
      const newWidth = (h * preset.width) / preset.height
      setWidth(Math.round(newWidth).toString())
    } else if (w > 0) {
      // Default: keep width, adjust height
      const newHeight = (w * preset.height) / preset.width
      setHeight(Math.round(newHeight).toString())
    }
  }

  const copyValue = async (value: string, label: string) => {
    await navigator.clipboard.writeText(value)
    setCopied(label)
    setTimeout(() => setCopied(null), 2000)
    toast.success(`Copied ${label}`)
  }

  const clear = () => {
    setWidth("")
    setHeight("")
    setRatioW(0)
    setRatioH(0)
    setLocked(null)
  }

  const w = parseFloat(width) || 0
  const h = parseFloat(height) || 0
  const decimalRatio = h > 0 ? (w / h).toFixed(2) : "0"
  const pixels = w * h

  // Common resolutions for current ratio
  const commonResolutions = [
    { w: 640, h: Math.round(640 * (ratioH / ratioW)) },
    { w: 1280, h: Math.round(1280 * (ratioH / ratioW)) },
    { w: 1920, h: Math.round(1920 * (ratioH / ratioW)) },
    { w: 2560, h: Math.round(2560 * (ratioH / ratioW)) },
    { w: 3840, h: Math.round(3840 * (ratioH / ratioW)) },
  ]

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Aspect Ratio Calculator
          </CardTitle>
          <CardDescription>
            Calculate and convert aspect ratios for images and videos
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Dimension Inputs */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="width">Width (px)</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7"
                  onClick={() => setLocked(locked === "width" ? null : "width")}
                >
                  {locked === "width" ? (
                    <Lock className="h-3 w-3 mr-1" />
                  ) : (
                    <Unlock className="h-3 w-3 mr-1" />
                  )}
                  {locked === "width" ? "Locked" : "Lock"}
                </Button>
              </div>
              <Input
                id="width"
                type="number"
                placeholder="1920"
                value={width}
                onChange={(e) => handleWidthChange(e.target.value)}
                className="font-mono"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="height">Height (px)</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7"
                  onClick={() => setLocked(locked === "height" ? null : "height")}
                >
                  {locked === "height" ? (
                    <Lock className="h-3 w-3 mr-1" />
                  ) : (
                    <Unlock className="h-3 w-3 mr-1" />
                  )}
                  {locked === "height" ? "Locked" : "Lock"}
                </Button>
              </div>
              <Input
                id="height"
                type="number"
                placeholder="1080"
                value={height}
                onChange={(e) => handleHeightChange(e.target.value)}
                className="font-mono"
              />
            </div>
          </div>

          {/* Clear Button */}
          <Button variant="outline" size="sm" onClick={clear}>
            <Trash2 className="h-4 w-4 mr-1" />
            Clear
          </Button>

          {/* Results */}
          {w > 0 && h > 0 && (
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="p-4 rounded-lg bg-muted/50 text-center">
                <p className="text-sm text-muted-foreground mb-1">Aspect Ratio</p>
                <p className="text-2xl font-bold font-mono">{ratioW}:{ratioH}</p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2"
                  onClick={() => copyValue(`${ratioW}:${ratioH}`, "ratio")}
                >
                  {copied === "ratio" ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                </Button>
              </div>
              <div className="p-4 rounded-lg bg-muted/50 text-center">
                <p className="text-sm text-muted-foreground mb-1">Decimal</p>
                <p className="text-2xl font-bold font-mono">{decimalRatio}</p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2"
                  onClick={() => copyValue(decimalRatio, "decimal")}
                >
                  {copied === "decimal" ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                </Button>
              </div>
              <div className="p-4 rounded-lg bg-muted/50 text-center">
                <p className="text-sm text-muted-foreground mb-1">Total Pixels</p>
                <p className="text-2xl font-bold font-mono">{pixels.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {(pixels / 1000000).toFixed(2)} MP
                </p>
              </div>
            </div>
          )}

          {/* Preview Box */}
          {w > 0 && h > 0 && (
            <div className="flex justify-center">
              <div
                className="border-2 border-primary/50 rounded flex items-center justify-center text-sm text-muted-foreground"
                style={{
                  width: Math.min(300, 300 * (w / Math.max(w, h))),
                  height: Math.min(300, 300 * (h / Math.max(w, h))),
                }}
              >
                {width} × {height}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preset Ratios */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Common Aspect Ratios</CardTitle>
          <CardDescription>Click to apply a preset ratio</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {presetRatios.map((preset) => (
              <Button
                key={preset.name}
                variant="outline"
                className="h-auto py-3 flex flex-col items-start"
                onClick={() => applyPreset(preset)}
              >
                <span className="font-mono font-bold">{preset.name}</span>
                <span className="text-xs text-muted-foreground">{preset.description}</span>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Resolutions for current ratio */}
      {ratioW > 0 && ratioH > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Common Resolutions ({ratioW}:{ratioH})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm">
              {commonResolutions.map((res) => (
                <Button
                  key={res.w}
                  variant="ghost"
                  className="font-mono justify-center"
                  onClick={() => {
                    setWidth(res.w.toString())
                    setHeight(res.h.toString())
                  }}
                >
                  {res.w}×{res.h}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
